import React from "react";

import { tw } from "~/utils";

export interface Segment {
  id: string;
  label: string;
}

export const SIGNUP_SEGMENTS: Segment[] = [
  { id: "about_you", label: "About You" },
  { id: "insurance", label: "Insurance" },
  { id: "details", label: "Details" },
  { id: "book", label: "Book" },
];

// Map form steps to segments
export const STEP_TO_SEGMENT: Record<string, string> = {
  // About You segment
  health_conditions: "about_you",
  full_name: "about_you",
  // Insurance segment
  primary_insurance: "insurance",
  secondary_insurance: "insurance",
  // Preferences segment
  state: "details",
  visit_type: "details",
  office_preference: "details",
  // Details segment
  email: "details",
  phone: "details",
  referred_by: "details",
  other_info: "details",
  // Book segment
  provider_selection: "book",
  confirmation: "book",
};

interface SegmentedProgressProps {
  currentStepId: string;
  completedSegments?: string[];
  className?: string;
}

export const SegmentedProgress: React.FC<SegmentedProgressProps> = ({
  currentStepId,
  completedSegments = [],
  className,
}) => {
  const currentSegmentId = STEP_TO_SEGMENT[currentStepId] || "about_you";
  const currentSegmentIndex = SIGNUP_SEGMENTS.findIndex(
    (s) => s.id === currentSegmentId,
  );

  return (
    <div className={tw("w-full", className)}>
      <div className="flex items-center justify-between">
        {SIGNUP_SEGMENTS.map((segment, index) => {
          const isCompleted =
            completedSegments.includes(segment.id) ||
            index < currentSegmentIndex;
          const isCurrent = segment.id === currentSegmentId;
          const isUpcoming = index > currentSegmentIndex;

          return (
            <React.Fragment key={segment.id}>
              {/* Dot */}
              <div className="flex flex-col items-center">
                <div
                  className={tw(
                    "flex size-5 items-center justify-center rounded-full border-2 transition-all duration-300 sm:size-6",
                    isCompleted && "border-green-500 bg-green-500",
                    isCurrent && !isCompleted && "border-green-500 bg-white",
                    isUpcoming && "border-gray-300 bg-white",
                  )}
                >
                  {isCompleted && (
                    <svg
                      className="size-3 text-white sm:size-3.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {isCurrent && !isCompleted && (
                    <div className="size-2 rounded-full bg-green-500 sm:size-2.5" />
                  )}
                </div>
                {/* Label */}
                <span
                  className={tw(
                    "mt-1 text-[10px] font-medium transition-colors sm:mt-1.5 sm:text-xs",
                    isCompleted && "text-green-600",
                    isCurrent && !isCompleted && "text-green-600",
                    isUpcoming && "text-gray-400",
                  )}
                >
                  {segment.label}
                </span>
              </div>

              {/* Connector line (not after last item) */}
              {index < SIGNUP_SEGMENTS.length - 1 && (
                <div
                  className={tw(
                    "mx-1 -mt-5 h-0.5 flex-1 transition-colors duration-300",
                    index < currentSegmentIndex
                      ? "bg-green-500"
                      : "bg-gray-300",
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default SegmentedProgress;
